import { useRef, useState } from 'react';
import {
  Box,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  Portal,
} from '@mui/material';
import SaveAltTwoToneIcon from '@mui/icons-material/SaveAltTwoTone';
import DownloadingIcon from '@mui/icons-material/Downloading';
import downloadHelper from '../../utils/downloadHelper';

const ImageViewer = ({ imagefile, width = 320 }) => {
  const baseSrc =
    imagefile.surl || imagefile.url || imagefile.cdn_url || imagefile.turl;
  const src = baseSrc || '';

  const lref = useRef();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });
  const [downloading, setDownloading] = useState(false);
  const [touchStarted, setTouchStarted] = useState(false);

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const onClickDownload = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDownloading(true);
    const { message, status } = await downloadHelper(src, 'image');

    showSnackbar(message, status);
    setDownloading(false);
    setTouchStarted(false); // Reset touch state
  };

  const handleTouchStart = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setTouchStarted(true);
  };

  const handleTouchEnd = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (touchStarted && !downloading) {
      await onClickDownload(event);
    }
  };

  // const onClickOpen = (event) => {
  //   event.stopPropagation();
  //   const openUrl = getUrlWithWidth(src);

  //   if (!Capacitor.isNativePlatform()) {
  //     lref.current.href = openUrl;
  //     lref.current.click();
  //   }
  // };

  const buttonBaseStyle = {
    fontSize: '0.70rem',
    borderRadius: 2,
    color: 'white',
    minHeight: 28,
    minWidth: 28,
    padding: '2px 8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    lineHeight: 1.2,
  };

  // const openButtonStyle = {
  //   ...buttonBaseStyle,
  //   backgroundColor: '#0ABAB5',
  //   '&:hover': {
  //     backgroundColor: '#0ab1ac',
  //   },
  // };

  const downloadButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#4ea5f9',
    '&:hover': {
      backgroundColor: '#509af4',
    },
    touchAction: 'manipulation',
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    userSelect: 'none',
  };

  // const zoomButtonStyle = {
  //   ...buttonBaseStyle,
  //   backgroundColor: "#607d8b",
  //   "&:hover": {
  //     backgroundColor: "#455a64",
  //   },
  // };

  return (
    <Box sx={{ width: '100%', maxWidth: width, margin: '0 auto' }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'auto',
          textAlign: 'center',
          mb: 1,
        }}
      >
        <img
          src={src}
          alt={imagefile.filename || 'Image'}
          style={{
            width: '600px',
            maxWidth: '100%',
            borderRadius: 8,
            background: '#f5f5f5',
            transition: 'width 0.2s',
            display: 'block',
            margin: '0 auto',
          }}
        />
        {downloading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255,255,255,0.5)',
              borderRadius: 8,
              zIndex: 9999,
            }}
          >
            <DownloadingIcon
              sx={{
                fontSize: 48,
                color: 'primary.main',
                animation: 'pulse 1s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 0.3 },
                  '50%': { opacity: 1 },
                  '100%': { opacity: 0.3 },
                },
              }}
            />
          </Box>
        )}
      </Box>
      <Grid
        container
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ mt: 1 }}
      >
        {/* <Grid item>
          <Tooltip title="Open" arrow>
            <IconButton size="small" onClick={(e) => onClickOpen(e)} sx={openButtonStyle}>
              <FileOpenIcon fontSize="small" sx={{ marginRight: 0.5 }} />
              <span style={{ fontSize: '0.70rem' }}>Open</span>
            </IconButton>
          </Tooltip>
        </Grid> */}
        {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
        <a
          ref={lref}
          style={{ display: 'none' }}
          href={src}
          target="_blank"
          rel="noreferrer"
        />
        <Grid item>
          {/* <Tooltip title="Download" arrow> */}
          <IconButton
            size="small"
            onClick={(e) => {
              // Only handle click if no touch events occurred
              setTimeout(() => {
                if (!touchStarted) {
                  onClickDownload(e);
                }
              }, 50);
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            sx={downloadButtonStyle}
            disabled={downloading}
          >
            <SaveAltTwoToneIcon fontSize="small" sx={{ marginRight: 0.3 }} />
            <span style={{ fontSize: '0.70rem' }}>Download</span>
          </IconButton>
          {/* </Tooltip> */}
        </Grid>
      </Grid>
      <Portal>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Portal>
    </Box>
  );
};

export default ImageViewer;
